class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/* ======================================================================
   HeaderMenu  -  forced click-only mega menu
   ----------------------------------------------------------------------
   The mega menu must open AND close only from a click - never from hover.
   This theme has hover code (in a file outside this script) that opens
   the panel on mouse-enter and closes it again on mouse-leave.

   Strategy: `isUserOpen` is the single source of truth for whether the
   menu should be open. It changes ONLY on genuine user actions:
       - a click / keyboard activation of the <summary>
       - a click outside the menu, the Escape key, or focus leaving it
   Hover events never touch it.

   Two layers keep the <details> `open` attribute pinned to `isUserOpen`:

     1. A MutationObserver on the `open` attribute. Its callback is a
        MICROTASK, so it runs BEFORE the browser paints. Reverting a
        hover-driven change here means no wrong-state frame is ever
        rendered - this is what removes the visible "jerk".

     2. The `toggle` event handler (onToggle), which fires as a TASK and
        could allow a paint, acts only as a backstop and drives the panel
        show / hide side effects once the state is settled.

   Visibility is additionally pinned with an inline `display !important`
   style - the top of the CSS cascade - so no theme stylesheet can show
   or hide the panel either.
   ====================================================================== */
class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.isMegaMenu = this.mainDetailsToggle.classList.contains('mega-menu');

    // Single source of truth: should the menu be open? Only real user
    // actions change this - hover events never do.
    this.isUserOpen = this.mainDetailsToggle.open;

    if (this.summary) {
      // A click on the summary (mouse, or Enter / Space on a focused
      // summary, which also dispatches a click) is the ONLY gesture that
      // may toggle the menu. The native <details> flips `open`; we flip
      // our intent flag to match.
      this.summary.addEventListener('click', () => {
        this.isUserOpen = !this.isUserOpen;
      });
    }

    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.setPanelHidden();

    // Layer 1: pre-paint guard. A hover handler that flips `open` would
    // otherwise paint one frame in the wrong state before the `toggle`
    // event could revert it - that frame is the "jerk". This observer
    // callback runs as a microtask (before paint), so the revert leaves
    // no rendered frame behind.
    this.openObserver = new MutationObserver(() => this.enforceOpenState());
    this.openObserver.observe(this.mainDetailsToggle, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }

  disconnectedCallback() {
    if (this.openObserver) this.openObserver.disconnect();
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('keydown', this.onKeyDown);
  }

  // Force the <details> open state back to the user's intent. Called from
  // the MutationObserver (microtask / pre-paint) and from onToggle.
  enforceOpenState() {
    if (this.mainDetailsToggle.open !== this.isUserOpen) {
      this.mainDetailsToggle.open = this.isUserOpen;
      return true; // a correction was made
    }
    return false;
  }

  // --- Forced visibility control (inline !important = top of cascade) ----
  setPanelHidden() {
    if (this.isMegaMenu && this.content) {
      this.content.style.setProperty('display', 'none', 'important');
    }
  }

  setPanelVisible() {
    if (this.isMegaMenu && this.content) {
      this.content.style.setProperty('display', 'block', 'important');
    }
  }

  onToggle() {
    if (!this.header) return;

    // Layer 2 backstop: if the observer has not already corrected a
    // hover-driven change, do it here. Setting `open` re-fires this
    // handler with a matching state, so there is no infinite loop.
    if (this.enforceOpenState()) return;

    const isOpen = this.isUserOpen;
    this.header.preventHide = isOpen;

    if (isOpen) {
      this.setPanelVisible();
      // Re-measure on EVERY open so a single bad reading can never get
      // cached and break max-height for the rest of the session.
      this.setHeaderBottomPosition();
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener('keydown', this.onKeyDown);
    } else {
      this.setPanelHidden();
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('keydown', this.onKeyDown);
    }
  }

  setHeaderBottomPosition() {
    const headerBottom = Math.floor(this.header.getBoundingClientRect().bottom);
    // Clamp so a wrong reading can never break the layout: never below 0
    // (header scrolled off-screen) and never above 60% of the viewport.
    const maxAllowed = Math.round(window.innerHeight * 0.6);
    const safeValue = Math.min(Math.max(headerBottom, 0), maxAllowed);
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${safeValue}px`
    );
  }

  onDocumentPointerDown(event) {
    // Close when a click / tap lands anywhere outside this menu.
    if (!this.contains(event.target)) this.close();
  }

  onKeyDown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      this.close();
      if (this.summary) this.summary.focus();
    }
  }

  close() {
    // Update intent FIRST so the toggle event fired by super.close() sees
    // a matching state and does not bounce the menu back open.
    this.isUserOpen = false;
    super.close();
    this.setPanelHidden();
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('keydown', this.onKeyDown);
  }
}

customElements.define('header-menu', HeaderMenu);